import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';
import { referencesApi } from '../services/backendApi';
import type { ProfessionalReference, ReferenceFormData } from '../types';

interface UseReferencesReturn {
  references: ProfessionalReference[];
  loading: boolean;
  error: string | null;
  fetchReferences: () => Promise<void>;
  createReference: (data: ReferenceFormData) => Promise<{ success: boolean; id?: string; error?: string }>;
  updateReference: (id: string, data: Partial<ReferenceFormData>) => Promise<{ success: boolean; error?: string }>;
  deleteReference: (id: string) => Promise<{ success: boolean; error?: string }>;
  clearError: () => void;
}

export const useReferences = (): UseReferencesReturn => {
  const { isAuthenticated } = useAuth();
  const [references, setReferences] = useState<ProfessionalReference[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchReferences = useCallback(async () => {
    if (!isAuthenticated) return;

    setLoading(true);
    setError(null);

    try {
      const data = await referencesApi.getAll();
      setReferences(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load references');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchReferences();
  }, [fetchReferences]);

  const createReference = useCallback(async (data: ReferenceFormData) => {
    setLoading(true);
    setError(null);

    try {
      const created = await referencesApi.create(data);
      setReferences(prev => [created, ...prev]);
      return { success: true, id: created.id };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create reference';
      setError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  }, []);

  const updateReference = useCallback(async (id: string, data: Partial<ReferenceFormData>) => {
    setLoading(true);
    setError(null);

    try {
      const updated = await referencesApi.update(id, data);
      setReferences(prev => prev.map(r => (r.id === id ? updated : r)));
      return { success: true };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update reference';
      setError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteReference = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);

    try {
      await referencesApi.remove(id);
      setReferences(prev => prev.filter(r => r.id !== id));
      return { success: true };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete reference';
      setError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  }, []);

  const clearError = useCallback(() => setError(null), []);

  return {
    references,
    loading,
    error,
    fetchReferences,
    createReference,
    updateReference,
    deleteReference,
    clearError,
  };
};
